import { toast } from "react-toastify";
import { observable } from "mobx";
import { api, apiOperacao } from "../../api";
import { toast_error } from "../../utils";

/**
 * Classe responsavel pelo gerenciamento do CNDS
*/
export class _CndsStore {
  obs = observable({
    tasks_cnds: [] as any[],
    tasks_length: 0 as number,
    loading_solicitacao: false as boolean,
    loading_arquivos: false as boolean,
    loading_tasks: false as boolean,
    open_solicitar: false as boolean,
    page_agendamentos: 1 as number,
    index_agendamentos: [] as any,
    length_agendamentos: 0 as number,
    open_iframe: false as boolean,
    base64: "" as string,
    agendamentos_filter: false as boolean,

    credito_usados: 0 as number,
    credito_contratado: 0 as number,
    qrcode: "" as string,
    pix_id: "" as string,
    loading: false as boolean,

  })

  /**
  * Busca e gerencia as solicitações relacionadas a uma empresa com base em seu CNPJ.
  * Realiza a chamada à API para obter os dados paginados e atualiza os estados observáveis com os resultados.
  * 
  * @param {number} paged Número da página a ser buscada.
  * @param {string} cnpj CNPJ da empresa para filtrar as solicitações.
  * 
  * @example
  * const params = {
  *   page: 1,
  *   limit: 5,
  *   cnpj: "10770872000192"
  * };
  *
  * post('/cnds/index', params);
  */
  async handleTasksPaged(paged: number, cnpj: string) {
    try {

      let data = {
        page: paged,
        limit: 15,
        cnpj: cnpj
      }

      let resp = await apiOperacao("cnds").post("/cnds/index", data);

      this.obs.tasks_cnds = [];
      this.obs.tasks_length = 0

      if (resp.data.itens.length == 0) {

        toast.warning('Nenhuma solicitação foi encontrada, tente novamente mais tarde.', {});

      } else {

        this.obs.tasks_cnds = resp.data.itens;

        this.obs.tasks_length = resp.data.count;

      }

    } catch (ex) { }

    this.obs.loading_tasks = false
  };

  /**
  * Realiza a busca e atualização da lista de agendamentos.
  * 
  * Este método faz uma requisição à API para obter os agendamentos com base em filtros predefinidos.
  * Os resultados são armazenados nos observáveis correspondentes para exibição e manipulação no sistema.
  * 
  * @param {number} page Número da página que será buscada.
  * 
  * @example
  * const params = {
  *   page: 1,
  *   limit: 5,
  *   recorrente: true
  * };
  *
  * post('/cnds/agendamento/index', params);
  */
  async handleIndexAgendamentos(page: number) {

    try {
      let params = {
        page: page,
        limit: 5,
        recorrente: this.obs.agendamentos_filter
      }

      let resp = await apiOperacao("cnds").post(`/cnds/agendamento/index`, params);

      this.obs.index_agendamentos = []
      this.obs.length_agendamentos = 0

      if (resp.data.itens.length > 0) {

        this.obs.index_agendamentos = resp.data.itens

        this.obs.length_agendamentos = resp.data.count

      }
    } catch(e){ }
  };

  /**
   * Cria um agendamento de CNDS com os itens e informações fornecidas.
   * @param {any} params Um mapa contendo os dados do agendamento, como `dia`, `mes`, `ano`, etc. 
   * 
   * @example
   * const params = {
   *   [{cnpj: "12345678000190", ie: "123456789"}],
   *   dia: 15,
   *   mes: 12,
   *   ano: 2024,
   *   recorrente: false,
   *   gratuito: false
   * }
   * 
   * post('/cnds/create', params);
   */
  async handleSolicitaCnds(params: any) {

    this.obs.loading_solicitacao = true

    try {

      let resp = await apiOperacao("cnds").post("/cnds/create", params);

      if (resp.data.ok) {

        toast.success('Solicitação realizada com sucesso!', {});

      } else {

        toast.error(resp.data.msg, {});

      }

    } catch (ex) {

      toast_error(ex, `Erro ao realizar Solicitação, tente novamente mais tarde.`);

    }

    this.obs.loading_solicitacao = false

  };

  /**
   * Deleta um agendamento relacionado à CNDS.
   * @param {string} id Identificador do agendamento a ser deletado.
   * 
   * @example
   * const id = "6761ce19453393b716b46f9b"
   *
   * delete(`/cnds/agendamento/${id}`)
   * 
   */
  async handleDeleteAgendamento(id: string) {
    try {

      let resp = await apiOperacao("cnds").delete(`/cnds/agendamento/${id}`);

      if (resp.data.ok) {

        toast.success('Agendamento deletado com sucesso.', {});
        this.handleIndexAgendamentos(1)

      } else {

        toast.error('Erro ao deletar agendamento, tente novamente mais tarde.', {});

      }

    } catch (ex) {

      toast_error(ex, `Erro ao deletar agendamento, tente novamente mais tarde.`);

    }
  };

  /**
   * Realiza o download de uma certidão CNDS.
   * @param {string} cnds_id Identificador da solicitação. 
   * @param {string} cnpj CNPJ da empresa.
   * @param {string} tipo Tipo da certidão ("receita", "tst", "caixa" e "sefa"). 
   * 
   * @example
   * const params = { 
   *   id: "6761ce19453393b716b46f9b"
   * }
   *
   * post(`/cnds/download`, params);
   * 
   */
  async downloadArquivos(cnds_id: string, cnpj: string, tipo: string) {

    this.obs.loading_arquivos = true

    try {

      let params = {
        id: cnds_id
      }

      let resp = await apiOperacao("cnds").post(`/cnds/download`, params);

      if (resp.data.file) {

        const linkSource = `data:application/pdf;base64,${resp.data.file}`;
        const downloadLink = document.createElement("a");
        const fileName = `${tipo}_${cnpj}.pdf`;

        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();

      } else {

        toast.error('Não foi possível realizar o download.', {});

      }

    } catch (ex) {

      toast_error(ex, `Erro Download Arquivo, tente novamente mais tarde.`);

    }

    this.obs.loading_arquivos = false

  };

  /**
   * Realiza a compra de créditos para realizar a solicitação da certidação "receita".
   * @param {number} quantidade Quantidade de créditos a serem comprados.
   * @returns {boolean} Retorna true` solicitção de crédito foi concluida e false` caso houve algum erro.
   * 
   * @example
   * 
   * const quantidade = 30
   * 
   * const params = { 
   *    quantidade: quantidade
   * }
   * 
   * post(`/cnds/buy/credits`, params);
   * 
   */
  async handleBuyQuantidade(quantidade: number) {

    this.obs.loading = true

    try {

      let params = {
        quantidade: quantidade
      }

      let resp = await apiOperacao("cnds").post("/cnds/buy/credits", params);

      if (resp.data.ok) {

        this.obs.qrcode = resp.data.qrcode
        this.obs.pix_id = resp.data.pix_id
        this.obs.loading = false
        return true

      } else {

        toast.error(resp.data.msg, {});
        this.obs.loading = false
        return false

      }

    } catch (ex) {

      toast_error(ex, `Erro ao processar Compra, tente novamente mais tarde.`);
      this.obs.loading = false
      return false

    }

  };

  /**
   * Consulta o saldo de créditos.
   * 
   * @example
   * get("/cnds/free/credits");
   * 
   */
  async handleConsultaCredito() {

    try {

      let resp = await apiOperacao("cnds").get("/cnds/free/credits");

      this.obs.credito_usados = resp.data.qtd_utilizada
      this.obs.credito_contratado = resp.data.qtd_contratada

    } catch (ex) {

      toast_error(ex, `Erro ao consultar Saldos, tente novamente mais tarde.`);

    }

  };

  /**
   * Atualiza um agendamento de CNDS com os itens e informações fornecidas.
   * @param {any} params Um mapa contendo os dados da solicitação, como `dia`, `mes`, `ano`, etc. 
   * @returns {boolean} Retorna `true` se o agendamento foi atualizado com sucesso, ou `false` caso tenha ocorrido algum erro.
   * 
   * @example
   * const params = {
   *   [{cnpj: "12345678000190", ie: "123456789"}],
   *   dia: 15,
   *   mes: 12,
   *   ano: 2024,
   *   recorrente: false,
   *   gratuito: false
   * }
   * 
   * post('/cnds/update', params);
   */
  async handleUpdateAgendamento(params: any) {

    this.obs.loading_solicitacao = true

    try {

      let resp = await apiOperacao("cnds").post("/cnds/update", params);

      if (resp.data.ok) {

        toast.success('Agendamento atualizado com sucesso!', {});

        return true

      } else {

        toast.error(resp.data.msg, {});

      }

    } catch (ex) {

      toast_error(ex, `Erro ao atualizar agendamento, tente novamente mais tarde.`);

    }

    return false

  };

}

export const CndsStore = new _CndsStore();
